import {
  ensure,
  required,
  type Match,
  type Slot,
  type Stage,
  type Tournament,
} from "./model";

export function possiblePoolCounts(teams: number): number[] {
  return Array.from({ length: teams }, (_, i) => i + 1).filter(
    (n) => Math.floor(teams / n) >= 4 && Math.ceil(teams / n) <= 6,
  );
}
export function seedOrder(size: number): number[] {
  ensure(
    size >= 2 && (size & (size - 1)) === 0,
    "A bracket must have a power-of-two size.",
  );
  let order = [1, 2];
  while (order.length < size) {
    const next = order.length * 2 + 1;
    order = order.flatMap((seed) => [seed, next - seed]);
  }
  return order;
}
function source(match: Match, outcome: "winner" | "loser" = "winner"): Slot {
  return { matchId: match.id, outcome };
}
export function makeMatch(
  t: Tournament,
  slots: [Slot, Slot],
  stage: Stage,
  round: number,
  wave: number,
  poolId?: string,
): Match {
  const match: Match = {
    id: `m${++t.nextMatchNumber}`,
    slots,
    stage,
    round,
    wave,
    poolId,
    teams: [null, null],
    status: "waiting",
    points: [0, 0],
    sets: [],
    refereeMode: "auto",
    needsRelease: t.config.releaseStages.includes(stage),
    released: false,
    notBefore: t.config.notBefore[stage],
    revision: 0,
  };
  t.matches.push(match);
  return match;
}
function pair(
  t: Tournament,
  slots: Slot[],
  stage: Stage,
  round: number,
  wave: number,
): Match[] {
  const result: Match[] = [];
  for (let i = 0; i < slots.length; i += 2)
    result.push(
      makeMatch(
        t,
        [required(slots[i]), required(slots[i + 1])],
        stage,
        round,
        wave,
      ),
    );
  return result;
}
export function elimination(
  t: Tournament,
  ranked: string[],
  double = false,
): void {
  ensure(ranked.length >= 2, "At least two teams must qualify.");
  const size = 2 ** Math.ceil(Math.log2(ranked.length));
  const slots: Slot[] = seedOrder(size).map((seed) =>
    ranked[seed - 1] ? { teamId: ranked[seed - 1]! } : { bye: true },
  );
  let wave = Math.max(0, ...t.matches.map((m) => m.wave)) + 1;
  if (!double) {
    let current = slots;
    let round = 1;
    let semis: Match[] = [];
    while (current.length > 1) {
      const stage =
        current.length === 2
          ? "final"
          : current.length === 4
            ? "semifinal"
            : "playoff";
      const matches = pair(t, current, stage, round++, wave++);
      if (stage === "semifinal") semis = matches;
      current = matches.map((m) => source(m));
    }
    if (t.config.bronze && semis.length === 2)
      pair(
        t,
        semis.map((m) => source(m, "loser")),
        "bronze",
        round - 1,
        wave - 1,
      );
    return;
  }
  let winners = pair(t, slots, "winner", 1, wave++);
  let losers = pair(
    t,
    winners.map((m) => source(m, "loser")),
    "loser",
    1,
    wave++,
  );
  let round = 2;
  while (winners.length > 2) {
    winners = pair(
      t,
      winners.map((m) => source(m)),
      "winner",
      round,
      wave++,
    );
    const drops = winners.map((m) => source(m, "loser"));
    losers = losers.map((m, i) =>
      makeMatch(
        t,
        [source(m), required(drops[i])],
        "loser",
        round * 2 - 2,
        wave,
      ),
    );
    wave++;
    if (losers.length > 2)
      losers = pair(
        t,
        losers.map((m) => source(m)),
        "loser",
        round * 2 - 1,
        wave++,
      );
    round++;
  }
  const winnerSemi = makeMatch(
    t,
    [source(required(winners[0])), source(required(winners[1]))],
    "semifinal",
    round,
    wave,
  );
  const loserSemi = makeMatch(
    t,
    [source(required(losers[0])), source(required(losers[1]))],
    "semifinal",
    round,
    wave++,
  );
  makeMatch(
    t,
    [source(winnerSemi), source(loserSemi)],
    "final",
    round + 1,
    wave,
  );
  if (t.config.bronze)
    makeMatch(
      t,
      [source(winnerSemi, "loser"), source(loserSemi, "loser")],
      "bronze",
      round + 1,
      wave,
    );
}

export function poolDraw(t: Tournament): void {
  const count = t.config.poolCount;
  ensure(
    possiblePoolCounts(t.teams.length).includes(count),
    "Pools must have four to six teams and differ in size by at most one.",
  );
  const teams = t.teams.toSorted((a, b) => a.seed - b.seed);
  t.pools = Array.from({ length: count }, (_, i) => {
    const teamIds = teams
      .filter((_, j) => j % count === i)
      .map((team) => team.id);
    const owned = t.courts.find(
      (c) => c.netTeamId && teamIds.includes(c.netTeamId),
    );
    return {
      id: String.fromCharCode(65 + i),
      teamIds,
      courtId: (owned ?? required(t.courts[i % t.courts.length])).id,
    };
  });
  for (const pool of t.pools) {
    const rotating: Array<string | null> = [...pool.teamIds];
    if (rotating.length % 2) rotating.push(null);
    for (let round = 1; round < rotating.length; round++) {
      for (let i = 0; i < rotating.length / 2; i++) {
        const a = rotating[i];
        const b = rotating[rotating.length - 1 - i];
        if (!a || !b) continue;
        const m = makeMatch(
          t,
          [{ teamId: a }, { teamId: b }],
          "pool",
          round,
          round,
          pool.id,
        );
        m.courtId = pool.courtId;
      }
      rotating.splice(1, 0, required(rotating.pop()));
    }
  }
}

export function resolveMatches(t: Tournament): void {
  const slotValue = (
    slot: Slot,
  ): { resolved: boolean; teamId: string | null } => {
    if ("teamId" in slot) return { resolved: true, teamId: slot.teamId };
    if ("bye" in slot) return { resolved: true, teamId: null };
    const match = required(t.matches.find((m) => m.id === slot.matchId));
    const teamId =
      (slot.outcome === "winner" ? match.winnerId : match.loserId) ?? null;
    return {
      resolved: match.status === "complete",
      teamId: t.teams.some((team) => team.id === teamId && team.withdrawn)
        ? null
        : teamId,
    };
  };
  for (const match of t.matches) {
    if (match.status === "complete" || match.status === "live") continue;
    const a = slotValue(match.slots[0]);
    const b = slotValue(match.slots[1]);
    match.teams = [a.teamId, b.teamId];
    if (!a.resolved || !b.resolved) {
      match.status = "waiting";
      continue;
    }
    if (!a.teamId || !b.teamId) {
      match.status = "complete";
      match.winnerId = a.teamId ?? b.teamId ?? undefined;
      match.resolution = "bye";
      const withdrawal = t.withdrawals.find(
        (w) => w.matchId === match.id && !w.replacementId,
      );
      if (withdrawal) {
        match.resolution = "forfeit";
        match.loserId = withdrawal.teamId;
      }
    } else match.status = "ready";
  }
}

export function descendants(t: Tournament, matchId: string): Match[] {
  const ids = new Set([matchId]);
  const result: Match[] = [];
  for (const match of t.matches) {
    if (match.slots.some((s) => "matchId" in s && ids.has(s.matchId))) {
      ids.add(match.id);
      result.push(match);
    }
  }
  return result;
}
